import { canonicalConditionFamily } from '../continuousSafety/conditionLifecyclePresentation';
import type { Policy } from '../../services/policiesApi';

export type SpatialOperationsPolicyView = {
  conditionLabel: string;
  scopeLabel: string;
  severity: string | null;
  immediateEnabled: boolean | null;
  patternEnabled: boolean | null;
  patternRule: string | null;
  scheduleLabel: string | null;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function familyFromPolicyType(policyType: string | null | undefined): string {
  return canonicalConditionFamily(policyType);
}

export function presentStoredPolicyPatternRule(
  minEvents: number | null | undefined,
  windowSeconds: number | null | undefined,
): string | null {
  if (minEvents == null || windowSeconds == null) return null;
  if (windowSeconds % 60 === 0) {
    const minutes = windowSeconds / 60;
    return `${minEvents} qualifying occurrences within ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${minEvents} qualifying occurrences within ${windowSeconds} seconds`;
}

export function presentScheduleLabel(schedule: Policy['schedule'] | null | undefined): string | null {
  if (!schedule) return null;
  if (schedule.enabled === false) return 'Always active';
  const timezone = text(schedule.timezone) || null;
  const windows = Array.isArray(schedule.windows) ? schedule.windows : [];
  const hasWindows = windows.length > 0
    || (Array.isArray(schedule.days) && schedule.days.length > 0)
    || Boolean(schedule.start_time || schedule.end_time);
  if (!schedule.enabled && !hasWindows) return null;
  if (!hasWindows) return timezone ? `Scheduled · ${timezone}` : 'Scheduled';
  return timezone ? `Scheduled · ${timezone}` : 'Scheduled';
}

export function selectPolicyForCondition(args: {
  policies: Policy[];
  conditionFamily?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
}): Policy | null {
  const family = canonicalConditionFamily(args.conditionFamily);
  const cameraId = text(args.cameraId);
  const zoneId = text(args.zoneId);
  const matches = args.policies.filter((policy) => {
    if (family && familyFromPolicyType(policy.policy_type) !== family) return false;
    const policyCamera = text(policy.camera_id || policy.target?.camera_id || policy.scope?.camera_ids?.[0]);
    if (cameraId && policyCamera && policyCamera !== cameraId) return false;
    const zoneIds = policy.scope?.zone_ids || [];
    if (zoneId && zoneIds.length && !zoneIds.includes(zoneId)) return false;
    return true;
  });
  return matches.find((policy) => policy.enabled !== false) || matches[0] || null;
}

export function presentSpatialOperationsPolicy(args: {
  policy: Policy | null;
  conditionLabel: string;
}): SpatialOperationsPolicyView | null {
  const policy = args.policy;
  if (!policy) return null;
  const zoneIds = policy.scope?.zone_ids || [];
  const cameraIds = policy.scope?.camera_ids || [];
  const scopeParts = [
    zoneIds.length ? `${zoneIds.length} zone${zoneIds.length === 1 ? '' : 's'}` : null,
    cameraIds.length || policy.camera_id ? 'camera scoped' : null,
  ].filter(Boolean);
  return {
    conditionLabel: args.conditionLabel,
    scopeLabel: scopeParts.join(' · ') || 'Site scope',
    severity: policy.alert?.severity || null,
    immediateEnabled: policy.alert?.enabled ?? null,
    patternEnabled: policy.pattern?.enabled ?? null,
    patternRule: presentStoredPolicyPatternRule(policy.pattern?.min_events, policy.pattern?.window_seconds),
    scheduleLabel: presentScheduleLabel(policy.schedule),
  };
}
